/**
 * Public Website Service
 * Handles API calls for public websites under *.website.fusionads.ai
 */

const WEBSITE_API_BASE_URL = import.meta.env.VITE_ASSISTANT_API_BASE_URL;

export interface AccountByDomainResponse {
	success: boolean;
	data: {
		accountId: string;
		name: string;
		domain: string;
		status: string;
		// Website generation step: 0 = generating content, 1 = generating images, 2 = complete
		websiteStep?: number;
	};
}

export interface Page {
	id: number;
	title: string;
}

export interface PagesResponse {
	success: boolean;
	data: {
		pages: Page[];
		pagination: {
			current_page: number;
			total_pages: number;
			total_items: number;
			items_per_page: number;
			has_next: boolean;
			has_previous: boolean;
		};
	};
	accountId: string;
}

export interface TrackingConfig {
	gtm_id?: string;
	gtag_id?: string;
	gtag_label?: string;
	phone?: string;
	fb_pixel_id?: string;
}

export interface RenderedPageResponse {
	success: boolean;
	data: {
		html: string;
		css?: string;
		javascript?: string;
		tracking?: TrackingConfig;
		meta?: {
			title?: string;
			description?: string;
			keywords?: string;
		};
	};
}

/**
 * Get account information by domain/subdomain
 * @param subdomain - The subdomain (e.g., "my-campaign" from "my-campaign.website.fusionads.ai")
 *                    or full domain (e.g., "test.fusionads.ai")
 */
export const getAccountByDomain = async (subdomain: string): Promise<AccountByDomainResponse> => {
	const response = await fetch(
		`${WEBSITE_API_BASE_URL}/api/website/accounts/by-domain/${encodeURIComponent(subdomain)}`,
		{
			method: 'GET',
			headers: {
				'Accept': '*/*',
				'Cache-Control': 'no-cache',
			},
		}
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch account: ${response.status}`);
	}

	return response.json();
};

/**
 * Get pages for an account
 * @param accountId - The account ID returned from getAccountByDomain
 */
export const getAccountPages = async (accountId: string): Promise<PagesResponse> => {
	const response = await fetch(
		`${WEBSITE_API_BASE_URL}/api/website/accounts/${encodeURIComponent(accountId)}/pages`,
		{
			method: 'GET',
			headers: {
				'Accept': '*/*',
				'Cache-Control': 'no-cache',
			},
		}
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch pages: ${response.status}`);
	}

	return response.json();
};

/**
 * Get rendered HTML for a specific page
 * @param accountId - The account ID
 * @param pageId - The page ID (typically 1 for the main page)
 */
export const getRenderedPage = async (accountId: string, pageId: number): Promise<RenderedPageResponse> => {
	const response = await fetch(
		`${WEBSITE_API_BASE_URL}/api/website/accounts/${encodeURIComponent(accountId)}/pages/${pageId}/rendered`,
		{
			method: 'GET',
			headers: {
				'Accept': '*/*',
				'Cache-Control': 'no-cache',
			},
		}
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch rendered page: ${response.status}`);
	}

	return response.json();
};

/**
 * Full flow: Get and render a public website
 * 1. Get account by domain
 * 2. Get pages for account
 * 3. Get rendered HTML for first page
 */
export const fetchPublicWebsite = async (subdomain: string): Promise<{
	html: string;
	css: string;
	javascript: string;
	tracking?: TrackingConfig;
	accountName: string;
	domain: string;
	websiteStep: number;
	meta?: {
		title?: string;
		description?: string;
		keywords?: string;
	};
}> => {
	// Step 1: Get account by domain
	const accountResponse = await getAccountByDomain(subdomain);
	
	if (!accountResponse.success || !accountResponse.data?.accountId) {
		throw new Error('Account not found');
	}

	const { accountId, name: accountName, domain, websiteStep = 0 } = accountResponse.data;

	// If websiteStep is 0, the website is still generating content - no pages yet
	if (websiteStep === 0) {
		return {
			html: '',
			css: '',
			javascript: '',
			accountName,
			domain,
			websiteStep,
		};
	}

	// Step 2: Get pages for account
	const pagesResponse = await getAccountPages(accountId);
	
	// If websiteStep is 1, we might not have all pages/images yet, but we should have content
	if (!pagesResponse.success || !pagesResponse.data?.pages?.length) {
		if (websiteStep === 1) {
			// Content generated but no pages yet - might be in transition
			return {
				html: '',
				css: '',
				javascript: '',
				accountName,
				domain,
				websiteStep,
			};
		}
		throw new Error('No pages found for this website');
	}

	// Get the first page (main page)
	const firstPage = pagesResponse.data.pages[0];
	
	if (!firstPage) {
		throw new Error('No pages available');
	}

	// Step 3: Get rendered HTML for the first page
	const renderedResponse = await getRenderedPage(accountId, firstPage.id);
	
	if (!renderedResponse.success || !renderedResponse.data?.html) {
		// If we have pages but can't render, still show the generation status
		if (websiteStep < 2) {
			return {
				html: '',
				css: '',
				javascript: '',
				accountName,
				domain,
				websiteStep,
			};
		}
		throw new Error('Failed to render page');
	}

	return {
		html: renderedResponse.data.html,
		css: renderedResponse.data.css || '',
		javascript: renderedResponse.data.javascript || '',
		tracking: renderedResponse.data.tracking,
		accountName,
		domain,
		websiteStep,
		meta: renderedResponse.data.meta,
	};
};

export default {
	getAccountByDomain,
	getAccountPages,
	getRenderedPage,
	fetchPublicWebsite,
};
